import { AuthService } from '@services/AuthService';
import { AuthSlice, IAuthState } from '@/store/reducers/AuthReducer';
import { AppDispatch } from '@/store/index';
import history from '@/router/history';
import { UserService } from '@services/UserService';
import { RouteNames } from '@/router/router.types';
import { getReturnUrl } from '@utils/helpers';
import { TwoFactorAuthenticationService } from '@services/TwoFactorAuthenticationService';
import { SamlAuthService } from '@services/SamlAuthService';
import { AxiosError } from 'axios';
import { UserAvatarSlice } from '@/store/reducers/UserAvatarReducer';
import { STORAGE_KEYS } from '@/constants';

export const AuthActionCreators = {
    login: (username: string, password: string, remember: boolean) => async (dispatch: AppDispatch) => {
        try {
            dispatch(AuthSlice.actions.setIsLoggingIn(true));

            const response = await AuthService.login(username, password);

            let returnUrl = getReturnUrl();

            localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.accessToken);

            if (remember) {
                localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken);
            } else {
                localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
            }

            dispatch(
                AuthSlice.actions.successLogin({
                    accessToken: response.data.accessToken,
                    refreshToken: response.data.refreshToken,
                    user: response.data.user,
                    returnUrl: returnUrl,
                } as IAuthState),
            );
        } catch (e) {
            console.log(e);
            let error = e as string;
            dispatch(AuthSlice.actions.setError({ message: error, login: username, loginType: 'LoginPass' }));
        } finally {
            dispatch(AuthSlice.actions.setIsLoggingIn(false));
        }
    },

    loginByHeader: () => async (dispatch: AppDispatch) => {
        try {
            dispatch(AuthSlice.actions.setIsLoggingIn(true));

            const response = await AuthService.loginByHeader();

            if (!response) {
                throw 'Не удалось выполнить вход через SSO';
            }

            let returnUrl = getReturnUrl();

            localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.accessToken);
            localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken);
            dispatch(
                AuthSlice.actions.successLogin({
                    accessToken: response.data.accessToken,
                    refreshToken: response.data.refreshToken,
                    user: response.data.user,
                    returnUrl: returnUrl,
                } as IAuthState),
            );
        } catch (e) {
            console.log(e);
            let error = e as string;
            dispatch(AuthSlice.actions.setError({ message: error, loginType: 'SSO' }));
        } finally {
            dispatch(AuthSlice.actions.setIsLoggingIn(false));
        }
    },

    loginBySaml: () => async (dispatch: AppDispatch) => {
        try {
            dispatch(AuthSlice.actions.setIsLoggingIn(true));

            const response = await SamlAuthService.loginAsync();

            if (!response) {
                throw 'Не удалось выполнить вход через ADFS';
            }

            let returnUrl = getReturnUrl();

            localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.accessToken);
            localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken);
            dispatch(
                AuthSlice.actions.successLogin({
                    accessToken: response.data.accessToken,
                    refreshToken: response.data.refreshToken,
                    user: response.data.user,
                    returnUrl: returnUrl,
                } as IAuthState),
            );
        } catch (e) {
            console.log(e);
            if (e instanceof AxiosError) {
                if (typeof e.response?.data === 'object') {
                    dispatch(
                        AuthSlice.actions.setError({ message: JSON.stringify(e.response?.data), loginType: 'SAML' }),
                    );
                } else {
                    dispatch(AuthSlice.actions.setError({ message: e.response?.data as string, loginType: 'SAML' }));
                }
            } else if (typeof e === 'object') {
                dispatch(AuthSlice.actions.setError({ message: JSON.stringify(e), loginType: 'SAML' }));
            } else {
                dispatch(AuthSlice.actions.setError({ message: e as string, loginType: 'SAML' }));
            }
        } finally {
            dispatch(AuthSlice.actions.setIsLoggingIn(false));
        }
    },

    logout: () => async (dispatch: AppDispatch) => {
        try {
            dispatch(AuthSlice.actions.setIsLoading(true));
            await AuthService.logout();
            localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
            dispatch(AuthSlice.actions.successLogout());
            dispatch(UserAvatarSlice.actions.clearAvatar());
            history.push(RouteNames.LOGIN);
        } catch (e) {
            console.log(e);
        } finally {
            dispatch(AuthSlice.actions.setIsLoading(false));
        }
    },

    logoutBySaml: () => async (dispatch: AppDispatch) => {
        try {
            dispatch(AuthSlice.actions.setIsLoading(true));
            localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
            dispatch(AuthSlice.actions.successLogout());
            dispatch(UserAvatarSlice.actions.clearAvatar());
        } catch (e) {
            console.log(e);
        }
    },

    getCurrentUser: (accessToken: string) => async (dispatch: AppDispatch) => {
        try {
            dispatch(AuthSlice.actions.setIsLoading(true));

            const [userResponse, settingsResponse] = await Promise.all([
                UserService.getCurrentUser(),
                AuthService.fetchSignInSettings(),
            ]);

            if (userResponse) {
                dispatch(
                    AuthSlice.actions.successLogin({
                        accessToken: accessToken,
                        user: userResponse.data,
                    } as IAuthState),
                );
            }

            if (settingsResponse) {
                dispatch(AuthSlice.actions.setSignInSettings(settingsResponse.data));
            }
        } catch (e) {
            console.log(e);
        } finally {
            dispatch(AuthSlice.actions.setIsLoading(false));
        }
    },

    setIsLoading: (flag: boolean) => async (dispatch: AppDispatch) => {
        dispatch(AuthSlice.actions.setIsLoading(flag));
    },

    setIsLoggingIn: (flag: boolean) => async (dispatch: AppDispatch) => {
        dispatch(AuthSlice.actions.setIsLoggingIn(flag));
    },

    resetReturnUrl: () => async (dispatch: AppDispatch) => {
        dispatch(AuthSlice.actions.resetReturnUrl());
    },

    loginTwoFactor:
        (service: TwoFactorAuthenticationService, username: string, password: string, login: () => void) =>
        (dispatch: AppDispatch) => {
            dispatch(AuthSlice.actions.setError({ message: '', login: username, loginType: 'LoginPassTwoFactor' }));
            dispatch(AuthSlice.actions.setIsLoggingIn(true));
            dispatch(AuthSlice.actions.setIsSmsOtp(false));

            service.login(
                username,
                password,
                () => {
                    login();
                },
                (msg) => {
                    console.log(msg);
                    dispatch(
                        AuthSlice.actions.setError({ message: msg, login: username, loginType: 'LoginPassTwoFactor' }),
                    );
                    dispatch(AuthSlice.actions.setIsLoggingIn(false));
                },
                () => {
                    dispatch(AuthSlice.actions.setIsSmsOtp(true));
                },
            );
        },
};
